import React, { useMemo } from 'react';
import DOMPurify from 'dompurify';
import MoodleLessonPageAnswers from './MoodleLessonPageAnswers';
import { useMoodleUi } from '../MoodleUiContext';
import { buildProxyHref } from '../../../../support/moodleHelpers';

interface MoodleLessonPageProps {
  lessonid: number;
  page: any;
  onJump: (pageid: number) => void;
  attachments?: any[];
}

const MoodleLessonPage: React.FC<MoodleLessonPageProps> = ({
                                                             lessonid,
                                                             page,
                                                             onJump,
                                                             attachments,
                                                           }) => {
  const { config } = useMoodleUi();
  const instance = config.instance;

  const html = page?.page?.contents || '';

  const referencedUrls = useMemo(() => {
    return Array.from(html.matchAll(/\b(?:src|href)=["']([^"']+)["']/gi), (m) => m[1]);
  }, [html]);

  const extraAttachments =
    attachments?.filter((f) => f.fileurl && !referencedUrls.includes(f.fileurl)) ?? [];

  return (
    <div className="lessonpage-wrapper">
      <h4 className="lessonpage-title">{page?.page?.title}</h4>

      <div
        className="lessonpage-content"
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }}
      />

      {page?.answerids && page.answerids.length > 0 && (
        <MoodleLessonPageAnswers
          lessonid={lessonid}
          pageid={page.page.id}
          jumps={page.jumps}
          onJump={onJump}
        />
      )}

      {extraAttachments.length > 0 && (
        <div className="attachments-section">
          <div className="attachments-title">Attachments:</div>
          <div className="attachments-list">
            {extraAttachments.map((file: any) => {
              const href = buildProxyHref(file.fileurl, instance);
              const isImage = file.mimetype?.startsWith('image/');

              return (
                <a
                  key={file.fileurl}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="attachment-link"
                >
                  {isImage ? (
                    <img
                      src={href}
                      alt={file.filename}
                      className="attachment-image"
                    />
                  ) : (
                    file.filename
                  )}
                </a>
              );
            })}
          </div>
        </div>
      )}

      <style>
        {`
          .lessonpage-wrapper {
            padding: 0;
          }

          .lessonpage-title {
            margin: 0 0 var(--hbr-spacing-sm) 0;
            font-family: var(--hbr-font-heading);
            font-size: var(--hbr-font-size-lg);
            font-weight: var(--hbr-font-weight-semibold);
            letter-spacing: var(--hbr-font-letter-spacing-dense);
            line-height: var(--hbr-font-line-height-dense);
          }

          .lessonpage-content {
            font-family: var(--hbr-font-body);
            font-size: var(--hbr-font-size-md);
            line-height: var(--hbr-font-line-height-normal);
          }

          .attachments-section {
            margin-top: var(--hbr-spacing-lg);
          }

          .attachments-title {
            font-family: var(--hbr-font-body);
            font-size: var(--hbr-font-size-sm);
            font-weight: var(--hbr-font-weight-semibold);
            margin-bottom: var(--hbr-spacing-xs);
          }

          .attachments-list {
            display: flex;
            flex-wrap: wrap;
            gap: var(--hbr-spacing-md);
          }

          .attachment-link {
            display: block;
            max-width: 200px;
            text-decoration: none;
            color: inherit;
          }

          .attachment-image {
            max-width: 100%;
            border-radius: 4px;
            box-shadow: 0 2px 6px #0002;
          }
        `}
      </style>
    </div>
  );
};

export default MoodleLessonPage;